import { useEffectOnce } from '@custom-react-hooks/use-effect-once';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { LlmPrompt } from '../../ifaces/prompt';
import { Skill } from '../../ifaces/skill';
import { fetchJson } from '../util/xhr';

/**
 * The Lowest level of all the LLM api helper functions
 *
 * All of the functions below this depend on this function, and this logs
 * each request to the console.
 *
 * Best used when:
 *   - You need programmatic access to the LLM api
 *   - You will handle the details and timing of the call manually
 *   - You want access to the raw Response promise
 *
 * See the `prefetch` implemention of various tool providers for examples.
 **/
export async function llmFetchJson<T = Response>(params: {
  name: LlmPrompt;
  skill: Skill['slug'];
  body: any;
}): Promise<T> {
  // const start = new Date()
  // console.info(`===== ${start.toISOString()} =====`);
  // console.info('REQUEST:', `${params.skill}/${params.name}`, JSON.stringify(params.body, null, 2));
  return fetchJson(`api/llm`, {
    skill: params.skill,
    name: params.name,
    ...params.body,
  }).then((json) => {
    // console.info('RESPONSE:', `${params.skill}/${params.name}`, JSON.stringify(json, null, 2));
    // const elapsed = new Date().getTime() - start.getTime()
    // console.info(`===== ${new Date().toISOString()} (${elapsed}ms) =====`)
    return json;
  });
}

/**
 * Speaking of `prefetch`, if you have a `Promise<Response>` that you want to consume
 * from a component. Pass that promise here, and you will recieve the data and a boolean
 * when the data is ready.
 *
 * See the components of providers that implement prefetch for an example.
 * */
export function useLlmPrefetched(response: Promise<Response>) {
  const [data, setData] = useState<any>({});
  const [done, setDone] = useState(false);
  useEffectOnce(() => {
    // console.log('LLM Prefetching data...');
    (async function () {
      const data = await response;
      // console.log('LLM Prefetched data:', data);
      setData(data);
      setDone(true);
    })();
  });
  return { data, done };
}

/**
 * Standard `useQuery` wrapper for fetching LLM output by prompt name.
 **/
export function useLlmQuery(
  props: {
    name: LlmPrompt;
    skill: Skill['slug'];
    body: any;
  },
  options: Partial<{
    enabled: boolean;
    queryKey: any;
  }> = {},
  inject?: any,
) {
  return useQuery({
    queryKey: ['llm', options.queryKey || [props.name, props.skill, props.body]],
    queryFn: async () => (inject ? inject : llmFetchJson(props)),
    enabled: options?.enabled === false ? false : true,
    staleTime: Infinity,
  });
}

/**
 * Deprecated, the first version of a hook used in the app.
 * Use `useLlmQuery` for any new features and code.
 **/
export function useLlmCall(props: {
  name: LlmPrompt;
  skill: Skill['slug'];
  body: any;
  setData?: (data: any) => void;
  setDone?: (done: boolean) => void;
}) {
  const [data, setDataCopy] = useState<any>({});
  const [done, setIsDone] = useState(false);
  useEffect(() => {
    setDataCopy({});
    setIsDone(false);
    const fetchData = async () => {
      try {
        const data = await llmFetchJson(props);
        if (props.setData) props.setData(data);
        setDataCopy(data);
        if (props.setDone) props.setDone(true);
        setIsDone(true);
      } catch (error) {
        console.error('Error fetching data:', error);
        if (props.setDone) props.setDone(true);
        setIsDone(true);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only run once
  return { data, done };
}
